import { VersionWatchdogService } from './core/services/version-watchdog.service';
import { IdleService } from './core/services/idle.service';
import { HeartbeatService } from './core/services/heartbeat.service';
import { Component, OnInit } from '@angular/core';
import { PlatformService } from './core/services/platform.service';
import { Router, NavigationError } from '@angular/router';
import { Title, DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { filter } from 'rxjs/operators';
import { SiteService } from './core/services/site.service';
import { CookieConsentService } from './core/services/cookie-consent.service';
import { Site } from './core/models/site.model';
import { PRIVACY_EU_HTML, PRIVACY_US_HTML, TERMS_EU_HTML, TERMS_US_HTML } from './core/constants/legal-content';
import { BUILD_INFO } from '../environments/build-info';
import { CHUNK_RELOAD_FLAG, CHUNK_RELOAD_GUARD_MS, STALE_CHUNK_EVENT } from './core/error-handler/global-error-handler';


@Component({
  selector: 'app-root',
  standalone: false,
  templateUrl: './app.html',
  styleUrls: ['./app.scss']
})
export class App implements OnInit {
  site: Site | null = null;
  loading = true;
  error: string | null = null;
  staleChunk = false;
  readonly currentYear = new Date().getFullYear();

  showPrivacyModal = false;
  showTermsModal = false;

  constructor(
    private siteService: SiteService,
    private router: Router,
    private titleService: Title,
    private cookieConsent: CookieConsentService,
    private sanitizer: DomSanitizer,
    private versionWatchdog: VersionWatchdogService,
    private idleService: IdleService,
    private heartbeatService: HeartbeatService,
    private platformService: PlatformService,
  ) {}

  get orgName(): string {
    return (this.site?.ext as any)?.orgName || this.site?.siteName || 'Location Pro';
  }

  get gdprEnabled(): boolean {
    return this.siteService.hasFeature('enableGdpr');
  }

  get isAdminRoute(): boolean {
    return this.router.url.startsWith('/admin');
  }

  get isLapland(): boolean {
    return this.siteService.hasFeature('isLaplandTenant');
  }

  get isGcfcSite(): boolean {
    return this.siteService.hasFeature('isGcfcTenant');
  }

  get hideLpCopyright(): boolean {
    return this.siteService.hasFeature('hideLpCopyright');
  }

  get hideWcagBadge(): boolean {
    return this.siteService.hasFeature('hideWcagBadge');
  }

  get privacyContent(): SafeHtml {
    // Safe: PRIVACY_EU_HTML / PRIVACY_US_HTML are compile-time constants in legal-content.ts,
    // authored by the LP engineering team and never derived from user input or runtime data.
    return this.sanitizer.bypassSecurityTrustHtml(this.gdprEnabled ? PRIVACY_EU_HTML : PRIVACY_US_HTML);
  }

  get termsContent(): SafeHtml {
    // Safe: TERMS_EU_HTML / TERMS_US_HTML are compile-time constants in legal-content.ts,
    // authored by the LP engineering team and never derived from user input or runtime data.
    return this.sanitizer.bypassSecurityTrustHtml(this.gdprEnabled ? TERMS_EU_HTML : TERMS_US_HTML);
  }

  buildInfo = BUILD_INFO;

  ngOnInit(): void {
    // Version stamp: console + DOM + footer — every screenshot self-identifies
    // as current or stale (the 2026-07-06 phantom-revert diagnosis tool).
    console.info(`LP build ${BUILD_INFO.sha} (${BUILD_INFO.builtAt})`);

    // Everything below this point is browser-only DOM/window bookkeeping,
    // client-side watchdogs, and legacy hash-URL handling — none of it makes
    // sense (and most of it would throw, since `document`/`window` are not
    // globals under SSR) while rendering on the server. The equivalent
    // client bootstrap re-runs this same ngOnInit after hydration.
    if (this.platformService.isBrowser) {
      try { document.documentElement.setAttribute('data-lp-build', BUILD_INFO.sha); } catch {}

      // Stale-tab watchdog: reload this tab (at an idle moment) when a newer
      // build is serving — no client ever runs old code indefinitely again.
      this.versionWatchdog.start();

      // Session idle timeout (4h, warns 60s before signing out) + heartbeat
      // (poll /auth/me every 5min while visible, recover in place on auth
      // failure) — app-wide, admin + public, no per-route wiring needed since
      // App is the top-level component for every route (2026-07-20, PR-C).
      // See IdleService/HeartbeatService doc comments for why signing out here
      // can never hard-reload or discard in-progress work.
      this.idleService.start();
      this.heartbeatService.start();

      // v3 legacy: the old site used HashLocationStrategy, generating URLs like
      // /#/lookbook/atlanticpicturesswamps?lb=391
      // vX uses PathLocationStrategy so Angular never sees the hash — intercept it
      // here and hard-redirect to the equivalent vX URL (/lookbook/391) before
      // the router or site init can send the user away to /locations.
      const hash = window.location.hash;
      const lbMatch = hash.match(/^#\/lookbook\/[^?#]*[?&]lb=(\d+)/);
      if (lbMatch) {
        window.location.replace(`/lookbook/${lbMatch[1]}`);
        return;
      }
    }

    this.cookieConsent.initFromStorage();
    this.initializeSite();
    this.cookieConsent.openPrivacy$.subscribe({
    next: () => { this.showPrivacyModal = true; },
    error: (_e: any) => { }
  });
    this.cookieConsent.openTerms$.subscribe({
    next: () => { this.showTermsModal = true; },
    error: (_e: any) => { }
  });
    this.router.events.pipe(
      filter(e => e instanceof NavigationError)
    ).subscribe({
    next: (e: any) => {
      if (e.error?.message?.includes('Failed to fetch dynamically imported module')) {
        this.staleChunk = true;
      }
    },
    error: (_e: any) => { }
  });
    // GlobalErrorHandler dispatches this when its reload-once recovery has
    // already been spent inside the guard window — surface the banner (whose
    // button does a cache-busting navigation) instead of dying silently.
    if (this.platformService.isBrowser) {
      window.addEventListener(STALE_CHUNK_EVENT, () => { this.staleChunk = true; });
    }
  }

  reloadApp(): void {
    // A plain reload can resurrect the heuristically-cached index.html whose
    // chunk references a deploy just removed — the app comes back half-stale
    // and lazy imports (e.g. the login modal) stay dead (Dave, 2026-07-06).
    // Navigating with a unique query forces a fresh document fetch.
    const base = window.location.pathname + window.location.search;
    const sep = base.includes('?') ? '&' : '?';
    window.location.href = `${base}${sep}_r=${Date.now()}`;
  }

  openCookiePreferences(): void {
    this.cookieConsent.openPreferences();
  }

  private initializeSite(): void {
    this.siteService.initializeSite().subscribe({
      next: (site) => {
        this.site = site;
        this.loading = false;
        if (typeof document !== 'undefined') {
          document.documentElement.lang = site.localeCode || 'en';
        }
        const pageTitle = (site.ext as any)?.orgName || site.siteName;
        if (pageTitle) this.titleService.setTitle(pageTitle);
        // Browser-only: the chunk-reload flag, and the root-path client
        // redirect, are both meaningless during SSR — the server renders
        // exactly the URL it was asked for and holds no sessionStorage.
        if (this.platformService.isBrowser) {
          // Successful load: once the app has stayed up for the full guard
          // window, clear the chunk-reload flag so the reload-once recovery is
          // re-armed for the NEXT deploy's chunk rotation. Cleared on a delay —
          // clearing immediately would allow a reload loop when the recovery
          // reload itself boots a still-dead chunk map.
          setTimeout(() => {
            try { sessionStorage.removeItem(CHUNK_RELOAD_FLAG); } catch { /* ignore */ }
          }, CHUNK_RELOAD_GUARD_MS);
          const currentPath = window.location.pathname;
          if (currentPath === '/' || currentPath === '') {
            if (site.features?.requiresLoginRedirect) {
              this.router.navigateByUrl('/login');
            } else if (!site.features?.isSowSite && !site.features?.isPittsburghTenant) {
              // Pittsburgh: HomeRedirectComponent handles the root redirect to /directory
              this.router.navigateByUrl('/locations');
            }
          }
        }
      },
      error: (err) => {
        this.error = 'Failed to load site configuration';
        this.loading = false;
      }
    });
  }
}
